import type { ComparedJourney, Comparison, IndicatorDelta } from '@ederest/contracts';
import { Card } from '../../shared/ui/Card.js';
import { ReadingList, type ReadingRow } from '../../shared/ui/ReadingList.js';
import { DirectionChip } from './DirectionChip.js';
import { effectSentence, formatIndicatorValue, isEffectMeasurable } from './comparison-format.js';
import './dashboard-summary.css';

interface ComparisonReadingProps {
  comparison: Comparison;
  guided: readonly ComparedJourney[];
  othersCount: number;
}

/** A label the page wrote in Arabic must not reorder the French sentence around it (first-strong isolate). */
const isolate = (text: string) => `⁨${text}⁩`;

/**
 * The comparison read in plain words before the tables: the effect in one
 * sentence, where it is measured, and why the other journeys are folded.
 * Every figure comes from the API; nothing is computed here.
 */
export function ComparisonReading({ comparison, guided, othersCount }: ComparisonReadingProps) {
  const measurable = isEffectMeasurable(comparison);
  const rows: ReadingRow[] = [
    {
      label: 'Effet',
      content: effectSentence(comparison),
      visual: measurable && <BeforeAfter delta={comparison.completionRate} />,
    },
  ];

  const [first] = guided;
  if (first) {
    const guidedStep = first.steps.find((step) => step.hasGuidance);
    const dropOff = guidedStep?.indicators.find((delta) => delta.key === 'drop_off_rate');
    rows.push({
      label: 'Où le lire',
      content: (
        <>
          Parcours « {isolate(first.journeyName)} »
          {guidedStep && <>, étape « {isolate(guidedStep.stepName)} »</>}
          {guided.length > 1 && ` et ${guided.length - 1} autre${guided.length > 2 ? 's' : ''}`}
          <span className="reading-list__sub">
            {dropOff
              ? `Abandon sur l'étape : ${formatIndicatorValue(dropOff.beforeValue, dropOff.unit)} avant, ${formatIndicatorValue(dropOff.afterValue, dropOff.unit)} après.`
              : "C'est là que l'aide publiée s'affiche."}
          </span>
        </>
      ),
      visual: dropOff && <DirectionChip delta={dropOff} />,
    });
  }

  if (othersCount > 0) {
    rows.push({
      label: 'Autres parcours',
      content: (
        <>
          {othersCount} parcours sans aide publiée, repliés plus bas.
          <span className="reading-list__sub">
            Leurs écarts ne viennent pas de cette version : ils servent de point de comparaison.
          </span>
        </>
      ),
    });
  }

  return (
    <div data-testid="comparison-reading">
      <Card title="En résumé" className="dash__section">
        <ReadingList rows={rows} />
      </Card>
    </div>
  );
}

function BeforeAfter({ delta }: { delta: IndicatorDelta }) {
  return (
    <span className="summary__effect">
      <span className="summary__effect-before">{formatIndicatorValue(delta.beforeValue, delta.unit)}</span>
      <span aria-hidden="true">→</span>
      <strong>{formatIndicatorValue(delta.afterValue, delta.unit)}</strong>
      <DirectionChip delta={delta} />
    </span>
  );
}
